import { TwoCol, SubSection } from "./fundCardHelpers";
import { InfoRow } from "../UI";
import { fmtMoney } from "../../parsers/xmlParser";
import { groupDeposits, pctOfSalary, lastDepositToDeposits } from "../../utils/depositsTable";

// תג קטן לסימון סוג הפקדה שאינו שוטף (ניוד / מענק / חד-פעמית).
function Badge({ children, title, tone = "amber" }) {
  const c = tone === "amber"
    ? { color: "#b45309", background: "#fef3c7", border: "#fde68a" }
    : { color: "#6d28d9", background: "#ede9fe", border: "#ddd6fe" };
  return (
    <span title={title} style={{
      fontSize: 10, fontWeight: 700, color: c.color, background: c.background,
      border: `1px solid ${c.border}`, borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

const chodeshFmt = (ym) => {
  const s = String(ym || "").replace(/\D/g, "");
  return s.length >= 6 ? `${s.slice(4, 6)}/${s.slice(0, 4)}` : (ym || "—");
};

// תא סכום + אחוז מהשכר (אם ידוע)
function AmtCell({ amount, salary }) {
  if (!amount) return <td style={tdNum}>—</td>;
  const pct = pctOfSalary(amount, salary);
  return (
    <td style={tdNum}>
      {fmtMoney(amount)}
      {pct != null && <span style={{ color: "#94a3b8", fontSize: 10 }}> ({pct}%)</span>}
    </td>
  );
}

const th = { padding: "6px 8px", fontSize: 10, fontWeight: 700, color: "#475569", whiteSpace: "nowrap", borderBottom: "1px solid #e2e8f0", textAlign: "right" };
const td = { padding: "5px 8px", fontSize: 11, color: "#0f172a", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" };
const tdNum = { ...td, direction: "ltr", textAlign: "right", fontVariantNumeric: "tabular-nums" };

function DepositsFlowTable({ deposits, accent }) {
  const rows = groupDeposits(deposits);
  if (!rows.length) return null;
  return (
    <div style={{ overflowX: "auto", marginBottom: 12 }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 720, direction: "rtl" }}>
        <thead>
          <tr>
            <th style={th}>סוג הפקדה</th>
            <th style={th}>מעסיק</th>
            <th style={th}>חודש שכר</th>
            <th style={th}>תאריך ערך</th>
            <th style={th}>שכר</th>
            <th style={th}>הפקדה עובד</th>
            <th style={th}>הפקדה מעסיק</th>
            <th style={th}>פיצויים</th>
            <th style={th}>סה״כ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={r.isTransfer ? { background: "#fffbeb" } : undefined}>
              <td style={td}>
                {r.isTransfer
                  ? <Badge title="ניוד כספים — לא נכלל בהקרנה">ניוד כספים</Badge>
                  : <span style={{ color: "#64748b" }}>{r.depositType || "שוטפת"}</span>}
              </td>
              <td style={{ ...td, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }} title={r.maasik || ""}>
                {r.maasik || "—"}
              </td>
              <td style={td}>{chodeshFmt(r.chodeshSachar)}</td>
              <td style={td}>{r.date || "—"}</td>
              <td style={tdNum}>{r.salary ? fmtMoney(r.salary) : "—"}</td>
              <AmtCell amount={r.oved} salary={r.salary} />
              <AmtCell amount={r.maavid} salary={r.salary} />
              <AmtCell amount={r.pitzuyim} salary={r.salary} />
              <td style={{ ...tdNum, fontWeight: 700, color: accent }}>{fmtMoney(r.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DepositsTab({ fund, cs }) {
  // מקור מועדף: תנועות מתוארכות. אם אין — בונים את הטבלה מ"הפקדה אחרונה" כדי
  // שהתצוגה תהיה אחידה לכל הקרנות.
  const flowDeposits = fund.flows?.deposits || [];
  const deposits = flowDeposits.length ? flowDeposits : lastDepositToDeposits(fund.lastDeposit);
  const hasTable = deposits.length > 0;

  return (
    <>
      {hasTable && (
        <SubSection title="הפקדות" color={cs.accent}>
          <DepositsFlowTable deposits={deposits} accent={cs.accent} />
        </SubSection>
      )}

      <TwoCol>
        <SubSection title="הפרשות חודשיות (שוטפת)" color={cs.accent}>
          <InfoRow label="שכר לחישוב" value={fund.employment?.salary} money />
          <InfoRow label="תאריך שכר" value={fund.employment?.salaryDate} />
          <InfoRow label="שיטת חישוב שכר" value={fund.employment?.salaryCalcMethod} />
          <InfoRow label="תחילת תשלום" value={fund.employment?.startDate} />
          <InfoRow label="סעיף 14" value={fund.employment?.seif14} />
          {fund.hafkadot?.map((h, i) => (
            <InfoRow key={i}
              label={`${h.type || "הפרשה"} (${h.payer})`}
              value={h.amount ? `${fmtMoney(h.amount)} / ${h.pct || 0}%` : h.pct ? `${h.pct}%` : null} />
          ))}
        </SubSection>
        <SubSection title="הפקדות שנה נוכחית" color={cs.accent}>
          <InfoRow label="הפקדות עובד" value={fund.annualDeposits?.oved} money />
          <InfoRow label="הפקדות מעביד" value={fund.annualDeposits?.maavid} money />
          <InfoRow label="הפקדות פיצויים" value={fund.annualDeposits?.pitzuim} money />
        </SubSection>
      </TwoCol>
    </>
  );
}
